import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { CircleNotch, Plus, Trash } from '@phosphor-icons/react';
import clsx from 'clsx';

import type {
  InviteMemberBody,
  OrganizationsResponse,
  OrgInvoice,
  OrgMemberRow,
  OrgMembersResponse,
  OrgRoleRow,
  OrgRow,
  OverviewResponse,
  OwnBrandFilingActionResult,
  RcsStatus,
  RcsUpgradeActionResult,
  PatchOrganizationBody,
  PatchOrganizationResponse,
  PlatformBillingPlan,
} from '../lib/types';
import { apiDelete, apiGet, apiPatch, apiPost, openViewAsSession } from '../lib/api';
import { formatCurrency, formatCount, formatMonth, formatPercent } from '../lib/format';
import {
  WAITING_ON_SHORT,
  durationLabel,
  lastCompleteMonth,
  marginPct,
  moneyFor,
  statusSubline,
} from '../lib/organizations';
import {
  ErrorState,
  KeyValueRow,
  LoadingState,
  MonthsSelect,
  PageHeader,
  Section,
  StatCard,
  TableScroll,
  useInsightsQuery,
} from '../components/ui';
import { CustomerActionsMenu, HealthPill, Pill, useCustomerActions, vendorTone } from '../components/organization';

// ---------------------------------------------------------------------------
// Presentational helpers
// ---------------------------------------------------------------------------

function StatusBadge({ value, positive }: { value: string; positive: boolean }) {
  return <Pill tone={positive ? 'ok' : 'neutral'}>{value}</Pill>;
}

const fieldClass =
  'w-full rounded-md border border-stone-200 bg-white px-2.5 py-1.5 text-sm text-stone-800 outline-none transition-colors hover:border-stone-300 focus:border-primary/40 focus:ring-2 focus:ring-primary/10';

const labelClass = 'mb-1 block text-[11px] font-medium uppercase tracking-wider text-stone-400';

function Field({
  label,
  children,
  hint,
}: {
  label: string;
  children: React.ReactNode;
  hint?: string;
}) {
  return (
    <label className="block">
      <span className={labelClass}>{label}</span>
      {children}
      {hint && <span className="mt-0.5 block text-[11px] text-stone-400">{hint}</span>}
    </label>
  );
}

function Toggle({
  checked,
  onChange,
  label,
  description,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  label: string;
  description?: string;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      onClick={() => onChange(!checked)}
      className="flex w-full items-start gap-3 rounded-lg border border-stone-200 bg-white px-3 py-2.5 text-left transition-colors hover:border-stone-300"
    >
      <span
        className={clsx(
          'mt-0.5 flex h-5 w-9 shrink-0 items-center rounded-full px-0.5 transition-colors',
          checked ? 'bg-primary' : 'bg-stone-200',
        )}
      >
        <span
          className={clsx(
            'h-4 w-4 rounded-full bg-white shadow transition-transform',
            checked ? 'translate-x-4' : 'translate-x-0',
          )}
        />
      </span>
      <span className="min-w-0">
        <span className="block text-sm font-medium text-stone-800">{label}</span>
        {description && <span className="mt-0.5 block text-xs text-stone-500">{description}</span>}
      </span>
    </button>
  );
}

// ---------------------------------------------------------------------------
// Settings form (profile fields mirror PATCH /organization/profile)
// ---------------------------------------------------------------------------

type BillingLeverKey = keyof PlatformBillingPlan;

const BILLING_LEVERS: Array<{ key: BillingLeverKey; label: string; step?: string }> = [
  { key: 'basePrice', label: 'Monthly fee with Ruta Pay ($)', step: '0.01' },
  { key: 'priceWithoutPayments', label: 'No Ruta Pay fee ($)', step: '0.01' },
  { key: 'starterTexts', label: 'One-time free texts' },
  { key: 'includedTexts', label: 'Monthly included texts' },
  { key: 'topUpTextsPrice', label: 'Overage per billed text ($)', step: '0.01' },
  { key: 'includedAiCredits', label: 'Included AI credits (unused)' },
  { key: 'topUpAiCreditsPrice', label: 'AI overage (unused, $)', step: '0.01' },
  { key: 'cardPercent', label: 'Card rate (%)', step: '0.01' },
  { key: 'cardFixed', label: 'Card fixed ($)', step: '0.01' },
  { key: 'achPercent', label: 'ACH rate (%)', step: '0.01' },
  { key: 'achCap', label: 'ACH cap ($)', step: '0.01' },
];

/** Pick a contracted rate by rate_type heuristics (Justifi naming varies). */
function rateFromContracted(
  rates: OrgRow['contractedRates'] | undefined,
  kind: 'card' | 'ach',
): { variableRate: string; transactionFeeCents: string } {
  const list = rates ?? [];
  const match = list.find((r) => {
    const t = r.rateType.toLowerCase();
    if (kind === 'ach') return t.includes('ach') || t.includes('bank');
    return t.includes('cc') || t.includes('card') || t.includes('ecomm') || t === 'visa' || t.includes('credit');
  });
  // Fallback: first non-ach for card
  const fallback =
    match ??
    (kind === 'card'
      ? list.find((r) => !r.rateType.toLowerCase().includes('ach'))
      : undefined);
  if (!fallback) return { variableRate: '', transactionFeeCents: '' };
  return {
    variableRate: String(fallback.variableRate),
    transactionFeeCents: String(fallback.transactionFeeCents),
  };
}

interface SettingsDraft {
  organizationName: string;
  messagingName: string;
  slug: string;
  website: string;
  customDomain: string;
  contactEmail: string;
  companyAddress: string;
  phoneNumber: string;
  isActive: boolean;
  subscriptionTier: string;
  subscriptionStatus: string;
  aiCreditLimit: string;
  billingOverrides: Record<BillingLeverKey, string>;
  requirePaymentMethod: boolean;
  autoSharePhotosDefault: boolean;
  notifyCustomerOnReviewDefault: boolean;
  cardVariableRate: string;
  cardTxnFeeCents: string;
  achVariableRate: string;
  achTxnFeeCents: string;
  achFeeCapCents: string;
  /** Seeded rates so we only PATCH feeRates when the operator changes them. */
  seededCardVariableRate: string;
  seededCardTxnFeeCents: string;
  seededAchVariableRate: string;
  seededAchTxnFeeCents: string;
}

function draftFromOrg(org: OrgRow): SettingsDraft {
  const overrides = org.billingPlan ?? {};
  const billingOverrides = {} as Record<BillingLeverKey, string>;
  for (const { key } of BILLING_LEVERS) {
    const v = overrides[key];
    billingOverrides[key] = v === undefined || v === null ? '' : String(v);
  }
  const card = rateFromContracted(org.contractedRates, 'card');
  const ach = rateFromContracted(org.contractedRates, 'ach');
  return {
    organizationName: org.name,
    messagingName: org.messagingName ?? '',
    slug: org.slug ?? '',
    website: org.website ?? '',
    customDomain: org.customDomain ?? '',
    contactEmail: org.contactEmail ?? '',
    companyAddress: org.companyAddress ?? '',
    phoneNumber: org.phoneNumber ?? '',
    isActive: org.isActive,
    subscriptionTier: org.subscriptionTier ?? '',
    subscriptionStatus: org.subscriptionStatus ?? '',
    aiCreditLimit:
      org.aiCreditLimit === null || org.aiCreditLimit === undefined ? '' : String(org.aiCreditLimit),
    billingOverrides,
    requirePaymentMethod: org.requirePaymentMethod !== false,
    autoSharePhotosDefault: org.autoSharePhotosDefault === true,
    notifyCustomerOnReviewDefault: org.notifyCustomerOnReviewDefault === true,
    cardVariableRate: card.variableRate,
    cardTxnFeeCents: card.transactionFeeCents,
    achVariableRate: ach.variableRate,
    achTxnFeeCents: ach.transactionFeeCents,
    // The webhook snapshot doesn't carry the cap, so it can't be seeded —
    // restate it whenever ACH rates are re-sent (blank = uncapped).
    achFeeCapCents: '',
    seededCardVariableRate: card.variableRate,
    seededCardTxnFeeCents: card.transactionFeeCents,
    seededAchVariableRate: ach.variableRate,
    seededAchTxnFeeCents: ach.transactionFeeCents,
  };
}

function overridesEqual(
  a: Partial<PlatformBillingPlan> | null,
  b: Partial<PlatformBillingPlan> | null,
): boolean {
  const aKeys = a ? Object.keys(a).sort() : [];
  const bKeys = b ? Object.keys(b).sort() : [];
  if (aKeys.length !== bKeys.length) return false;
  return aKeys.every((k) => a![k as BillingLeverKey] === b![k as BillingLeverKey]);
}

function nullIfEmpty(s: string): string | null {
  const t = s.trim();
  return t === '' ? null : t;
}

function buildPatchBody(org: OrgRow, draft: SettingsDraft): PatchOrganizationBody | null {
  const body: PatchOrganizationBody = {};

  if (draft.organizationName.trim() && draft.organizationName.trim() !== org.name) {
    body.organizationName = draft.organizationName.trim();
  }
  const nextMessaging = nullIfEmpty(draft.messagingName);
  if (nextMessaging !== (org.messagingName ?? null)) body.messagingName = nextMessaging;

  if (draft.slug.trim() && draft.slug.trim() !== (org.slug ?? '')) {
    body.slug = draft.slug.trim();
  }
  const nextWebsite = nullIfEmpty(draft.website);
  if (nextWebsite !== (org.website ?? null)) body.website = nextWebsite;

  const nextDomain = nullIfEmpty(draft.customDomain);
  if (nextDomain !== (org.customDomain ?? null)) body.customDomain = nextDomain;

  if (draft.contactEmail.trim() && draft.contactEmail.trim() !== (org.contactEmail ?? '')) {
    body.contactEmail = draft.contactEmail.trim();
  }
  const nextAddress = nullIfEmpty(draft.companyAddress);
  if (nextAddress !== (org.companyAddress ?? null)) body.companyAddress = nextAddress;
  const nextPhone = nullIfEmpty(draft.phoneNumber);
  if (nextPhone !== (org.phoneNumber ?? null)) body.phoneNumber = nextPhone;

  if (draft.isActive !== org.isActive) body.isActive = draft.isActive;

  const nextTier = draft.subscriptionTier || null;
  if (nextTier !== (org.subscriptionTier ?? null)) {
    body.subscriptionTier = nextTier as PatchOrganizationBody['subscriptionTier'];
  }
  const nextStatus = draft.subscriptionStatus || null;
  if (nextStatus !== (org.subscriptionStatus ?? null)) {
    body.subscriptionStatus = nextStatus as PatchOrganizationBody['subscriptionStatus'];
  }

  const nextAi =
    draft.aiCreditLimit.trim() === ''
      ? null
      : Math.max(0, Math.round(Number(draft.aiCreditLimit)));
  if (!Number.isNaN(nextAi as number) || draft.aiCreditLimit.trim() === '') {
    if (nextAi !== (org.aiCreditLimit ?? null)) body.aiCreditLimit = nextAi;
  }

  const nextOverrides: Partial<PlatformBillingPlan> = {};
  for (const { key } of BILLING_LEVERS) {
    const raw = draft.billingOverrides[key].trim();
    if (raw === '') continue;
    const n = Number(raw);
    if (Number.isNaN(n)) continue;
    nextOverrides[key] = n;
  }
  const hasAnyOverride = Object.keys(nextOverrides).length > 0;
  const nextPlan: Partial<PlatformBillingPlan> | null = hasAnyOverride ? nextOverrides : null;
  if (!overridesEqual(org.billingPlan ?? null, nextPlan)) {
    body.billingPlan = nextPlan;
  }

  if (draft.requirePaymentMethod !== (org.requirePaymentMethod !== false)) {
    body.requirePaymentMethod = draft.requirePaymentMethod;
  }
  if (draft.autoSharePhotosDefault !== (org.autoSharePhotosDefault === true)) {
    body.autoSharePhotosDefault = draft.autoSharePhotosDefault;
  }
  if (draft.notifyCustomerOnReviewDefault !== (org.notifyCustomerOnReviewDefault === true)) {
    body.notifyCustomerOnReviewDefault = draft.notifyCustomerOnReviewDefault;
  }

  // Fee rates: only send when the operator changed them from the seeded values.
  const feeRates: NonNullable<PatchOrganizationBody['feeRates']> = {};
  const cardChanged =
    draft.cardVariableRate !== draft.seededCardVariableRate ||
    draft.cardTxnFeeCents !== draft.seededCardTxnFeeCents;
  if (cardChanged && draft.cardVariableRate.trim() !== '') {
    const variableRate = Number(draft.cardVariableRate);
    const transactionFeeCents = Math.round(Number(draft.cardTxnFeeCents || '0'));
    if (!Number.isNaN(variableRate) && !Number.isNaN(transactionFeeCents)) {
      feeRates.card = { variableRate, transactionFeeCents };
    }
  }
  const achChanged =
    draft.achVariableRate !== draft.seededAchVariableRate ||
    draft.achTxnFeeCents !== draft.seededAchTxnFeeCents;
  if (achChanged && draft.achVariableRate.trim() !== '') {
    const variableRate = Number(draft.achVariableRate);
    const transactionFeeCents = Math.round(Number(draft.achTxnFeeCents || '0'));
    if (!Number.isNaN(variableRate) && !Number.isNaN(transactionFeeCents)) {
      feeRates.ach = { variableRate, transactionFeeCents };
      const feeCapCents = Math.round(Number(draft.achFeeCapCents));
      if (draft.achFeeCapCents.trim() !== '' && Number.isFinite(feeCapCents) && feeCapCents > 0) {
        feeRates.ach.feeCapCents = feeCapCents;
      }
    }
  }
  if (Object.keys(feeRates).length > 0) body.feeRates = feeRates;

  return Object.keys(body).length > 0 ? body : null;
}

function mergeUpdated(org: OrgRow, updated: PatchOrganizationResponse): OrgRow {
  return {
    ...org,
    ...updated,
    byMonth: org.byMonth,
    totals: org.totals,
  };
}

function OrgSettingsForm({
  org,
  defaults,
}: {
  org: OrgRow;
  defaults: PlatformBillingPlan;
}) {
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState<SettingsDraft>(() => draftFromOrg(org));
  const [error, setError] = useState<string | null>(null);
  const [savedFlash, setSavedFlash] = useState(false);

  useEffect(() => {
    setDraft(draftFromOrg(org));
    setError(null);
    setSavedFlash(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional orgId gate
  }, [org.orgId]);

  const patch = buildPatchBody(org, draft);
  const dirty = patch !== null;

  const mutation = useMutation({
    mutationFn: (body: PatchOrganizationBody) =>
      apiPatch<PatchOrganizationResponse>(`/organizations/${org.orgId}`, body),
    onSuccess: (updated) => {
      setError(null);
      setSavedFlash(true);
      const merged = mergeUpdated(org, updated);
      setDraft(draftFromOrg(merged));
      queryClient.setQueriesData<OrganizationsResponse>({ queryKey: ['organizations'] }, (prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          organizations: prev.organizations.map((row) =>
            row.orgId === updated.orgId ? mergeUpdated(row, updated) : row,
          ),
        };
      });
      // Status, health and the fee are worked out from these settings on the
      // server, and Home counts companies by them — fetch both fresh.
      void queryClient.invalidateQueries({ queryKey: ['organizations'] });
      void queryClient.invalidateQueries({ queryKey: ['overview'] });
      window.setTimeout(() => setSavedFlash(false), 2000);
    },
    onError: (err: Error) => setError(err.message),
  });

  const set = <K extends keyof SettingsDraft>(key: K, value: SettingsDraft[K]) => {
    setDraft((d) => ({ ...d, [key]: value }));
  };

  const setOverride = (key: BillingLeverKey, value: string) => {
    setDraft((d) => ({
      ...d,
      billingOverrides: { ...d.billingOverrides, [key]: value },
    }));
  };

  const hasSubAccount = Boolean(org.justifiSubAccountId);

  return (
    <>
      <Section title="Company profile" subtitle="Same fields as webapp company settings">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Field label="Legal name">
            <input
              className={fieldClass}
              value={draft.organizationName}
              onChange={(e) => set('organizationName', e.target.value)}
            />
          </Field>
          <Field label="Messaging name" hint="Customer-facing name in SMS/email">
            <input
              className={fieldClass}
              value={draft.messagingName}
              onChange={(e) => set('messagingName', e.target.value)}
              placeholder={draft.organizationName || 'Optional'}
            />
          </Field>
          <Field label="Slug" hint="Portal path · *.getruta.com">
            <input
              className={fieldClass}
              value={draft.slug}
              onChange={(e) => set('slug', e.target.value)}
            />
          </Field>
          <Field label="Website URL">
            <input
              className={fieldClass}
              value={draft.website}
              onChange={(e) => set('website', e.target.value)}
              placeholder="https://"
            />
          </Field>
          <Field label="Custom portal domain" hint="CNAME to Ruta · empty clears">
            <input
              className={fieldClass}
              value={draft.customDomain}
              onChange={(e) => set('customDomain', e.target.value)}
              placeholder="portal.example.com"
            />
          </Field>
          <Field label="Contact email">
            <input
              type="email"
              className={fieldClass}
              value={draft.contactEmail}
              onChange={(e) => set('contactEmail', e.target.value)}
            />
          </Field>
          <Field label="Phone">
            <input
              className={fieldClass}
              value={draft.phoneNumber}
              onChange={(e) => set('phoneNumber', e.target.value)}
            />
          </Field>
          <Field label="Company address">
            <input
              className={fieldClass}
              value={draft.companyAddress}
              onChange={(e) => set('companyAddress', e.target.value)}
            />
          </Field>
        </div>
        <div className="mt-3 grid grid-cols-1 gap-x-8 sm:grid-cols-2">
          <KeyValueRow label="Org ID" value={<code className="text-xs">{org.orgId}</code>} />
          <KeyValueRow label="Created" value={new Date(org.createdAt).toLocaleDateString()} />
          {org.customDomainStatus && (
            <KeyValueRow label="Domain status" value={org.customDomainStatus} />
          )}
        </div>
      </Section>

      <Section title="Payments (Justifi)" subtitle="Connection identity and processing rates">
        <div className="mb-4 grid grid-cols-1 gap-x-8 sm:grid-cols-2">
          <KeyValueRow
            label="RutaPay status"
            value={
              <StatusBadge
                value={org.rutaPayStatus}
                positive={org.rutaPayStatus === 'enabled' || org.rutaPayStatus === 'approved'}
              />
            }
          />
          <KeyValueRow
            label="Platform fee (tier list)"
            value={
              org.platformFeeMonthly === null
                ? '—'
                : `${formatCurrency(org.platformFeeMonthly)}/mo`
            }
          />
          <KeyValueRow
            label="Business ID"
            value={
              org.justifiBusinessId ? (
                <code className="text-xs">{org.justifiBusinessId}</code>
              ) : (
                '—'
              )
            }
          />
          <KeyValueRow
            label="Sub-account ID"
            value={
              org.justifiSubAccountId ? (
                <code className="text-xs">{org.justifiSubAccountId}</code>
              ) : (
                '—'
              )
            }
          />
        </div>

        {(org.contractedRates?.length ?? 0) > 0 && (
          <div className="mb-4">
            <div className="mb-1.5 text-[11px] font-medium uppercase tracking-wider text-stone-400">
              Contracted rates (from Justifi webhook)
            </div>
            <ul className="space-y-1 text-sm text-stone-700">
              {org.contractedRates.map((r) => (
                <li key={`${r.rateType}-${r.basisPointRate}`}>
                  <span className="font-medium">{r.rateType}</span>
                  {': '}
                  {r.variableRate.toFixed(2)}% + {formatCurrency(r.transactionFeeCents / 100)}
                </li>
              ))}
            </ul>
          </div>
        )}

        <div className="mb-2 text-[11px] font-medium uppercase tracking-wider text-stone-400">
          Override processing rates
        </div>
        <p className="mb-3 text-xs text-stone-500">
          Writes JustiFi Standard Fee Configurations on the sub-account
          (processing_ecomm / processing_ach). Requires a connected sub-account.
        </p>
        {!hasSubAccount && (
          <p className="mb-3 text-xs text-orange-800">
            No sub-account ID on file — connect RutaPay before overriding rates.
          </p>
        )}
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Field label="Card rate %" hint="processing_ecomm variable_rate">
            <input
              type="number"
              min={0}
              step={0.01}
              className={fieldClass}
              disabled={!hasSubAccount}
              value={draft.cardVariableRate}
              onChange={(e) => set('cardVariableRate', e.target.value)}
              placeholder="2.99"
            />
          </Field>
          <Field label="Card txn fee (¢)">
            <input
              type="number"
              min={0}
              step={1}
              className={fieldClass}
              disabled={!hasSubAccount}
              value={draft.cardTxnFeeCents}
              onChange={(e) => set('cardTxnFeeCents', e.target.value)}
              placeholder="30"
            />
          </Field>
          <Field label="ACH rate %" hint="processing_ach variable_rate">
            <input
              type="number"
              min={0}
              step={0.01}
              className={fieldClass}
              disabled={!hasSubAccount}
              value={draft.achVariableRate}
              onChange={(e) => set('achVariableRate', e.target.value)}
              placeholder="1.00"
            />
          </Field>
          <Field label="ACH txn fee (¢)">
            <input
              type="number"
              min={0}
              step={1}
              className={fieldClass}
              disabled={!hasSubAccount}
              value={draft.achTxnFeeCents}
              onChange={(e) => set('achTxnFeeCents', e.target.value)}
              placeholder="0"
            />
          </Field>
          <Field label="ACH fee cap (¢)" hint="blank = uncapped; a new config drops any prior cap">
            <input
              type="number"
              min={0}
              step={1}
              className={fieldClass}
              disabled={!hasSubAccount}
              value={draft.achFeeCapCents}
              onChange={(e) => set('achFeeCapCents', e.target.value)}
              placeholder="1500"
            />
          </Field>
        </div>
      </Section>

      <Section title="Commercial settings" subtitle="Platform plan, tier, and account status">
        <div className="space-y-4">
          <Toggle
            checked={draft.isActive}
            onChange={(isActive) => set('isActive', isActive)}
            label="Organization active"
            description="Inactive orgs are treated as dormant across insights and billing."
          />

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <Field label="Subscription tier">
              <select
                className={fieldClass}
                value={draft.subscriptionTier}
                onChange={(e) => set('subscriptionTier', e.target.value)}
              >
                <option value="">— none —</option>
                <option value="starter">Ruta</option>
                {/* Retired tiers: only offered when already stored, so the select
                    shows the truth; picking one again is pointless — both bill as Ruta. */}
                {(org.subscriptionTier === 'pro' || org.subscriptionTier === 'professional') && (
                  <option value={org.subscriptionTier}>Ruta Pro (retired — bills as Ruta)</option>
                )}
                {org.subscriptionTier === 'growth' && (
                  <option value="growth">Growth (retired — bills as Ruta)</option>
                )}
                <option value="enterprise">Enterprise</option>
              </select>
            </Field>
            <Field label="Subscription status">
              <select
                className={fieldClass}
                value={draft.subscriptionStatus}
                onChange={(e) => set('subscriptionStatus', e.target.value)}
              >
                <option value="">— none —</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
                <option value="payment_pending">Payment pending</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </Field>
            <Field label="AI credit limit" hint="Empty = no cap">
              <input
                type="number"
                min={0}
                step={1}
                className={fieldClass}
                value={draft.aiCreditLimit}
                onChange={(e) => set('aiCreditLimit', e.target.value)}
                placeholder="No limit"
              />
            </Field>
          </div>

          <div>
            <div className="mb-2 text-[11px] font-medium uppercase tracking-wider text-stone-400">
              Billing plan overrides
            </div>
            <p className="mb-3 text-xs text-stone-500">
              Leave blank to inherit the platform default. All blank clears overrides.
            </p>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {BILLING_LEVERS.map(({ key, label, step }) => (
                <Field key={key} label={label} hint={`Default ${defaults[key] ?? '—'}`}>
                  <input
                    type="number"
                    min={0}
                    step={step ?? '1'}
                    className={fieldClass}
                    value={draft.billingOverrides[key]}
                    onChange={(e) => setOverride(key, e.target.value)}
                    placeholder={defaults[key] != null ? String(defaults[key]) : ''}
                  />
                </Field>
              ))}
            </div>
          </div>
        </div>
      </Section>

      <Section title="Operational settings" subtitle="How this company runs day-to-day">
        <div className="space-y-3">
          <Toggle
            checked={draft.requirePaymentMethod}
            onChange={(v) => set('requirePaymentMethod', v)}
            label="Require payment method"
            description="When off, no customer is required to have a card on file."
          />
          <Toggle
            checked={draft.autoSharePhotosDefault}
            onChange={(v) => set('autoSharePhotosDefault', v)}
            label="Auto-share visit photos"
            description="Org-wide default; service templates can still override."
          />
          <Toggle
            checked={draft.notifyCustomerOnReviewDefault}
            onChange={(v) => set('notifyCustomerOnReviewDefault', v)}
            label="Notify customer when visit enters review"
            description="When off, completion messages wait until review is finalized."
          />
        </div>
      </Section>

      {/* Spacer so fixed save bar doesn't cover the last section */}
      <div className="h-20" aria-hidden />

      {/* Fixed save bar — always visible while this org is open */}
      <div className="fixed inset-x-0 bottom-0 z-40 border-t border-stone-200 bg-white/95 px-4 py-3 backdrop-blur-sm shadow-[0_-4px_16px_rgb(0_0_0/0.06)]">
        <div className="mx-auto flex max-w-6xl flex-wrap items-center gap-3">
          <span className="mr-auto hidden text-sm text-stone-500 sm:inline truncate max-w-[40%]">
            {org.name}
          </span>
          <button
            type="button"
            disabled={!dirty || mutation.isPending}
            onClick={() => {
              const body = buildPatchBody(org, draft);
              if (!body) return;
              mutation.mutate(body);
            }}
            className={clsx(
              'inline-flex items-center gap-2 rounded-md px-4 py-2 text-sm font-medium transition-colors',
              dirty && !mutation.isPending
                ? 'bg-forest text-white hover:bg-forest/90'
                : 'cursor-not-allowed bg-stone-200 text-stone-500',
            )}
          >
            {mutation.isPending && <CircleNotch className="h-4 w-4 animate-spin" />}
            Save settings
          </button>
          {savedFlash && <span className="text-sm font-medium text-emerald-700">Saved</span>}
          {error && <span className="text-sm text-red-600">{error}</span>}
          {dirty && !mutation.isPending && (
            <span className="text-xs text-orange-800 font-medium">Unsaved changes</span>
          )}
          {!dirty && !savedFlash && !error && (
            <span className="text-xs text-stone-400">No changes to save</span>
          )}
        </div>
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------
// Team members (platform-operator org-scoped member endpoints)
// ---------------------------------------------------------------------------

function memberDisplayName(m: OrgMemberRow): string {
  const name = [m.firstName, m.lastName].filter(Boolean).join(' ').trim();
  return name || m.email || m.phoneNumber || m.id;
}

function MembersSection({ orgId }: { orgId: string }) {
  const queryClient = useQueryClient();
  const membersKey = ['org-members', orgId];
  const rolesKey = ['org-roles', orgId];

  const membersQuery = useInsightsQuery<OrgMembersResponse>(
    membersKey,
    `/organizations/${orgId}/members`,
  );
  const rolesQuery = useQuery({
    queryKey: rolesKey,
    queryFn: () => apiGet<OrgRoleRow[]>(`/organizations/${orgId}/roles`),
  });

  const [inviteOpen, setInviteOpen] = useState(false);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [roleId, setRoleId] = useState('');
  const [skipInvitation, setSkipInvitation] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [actionMessage, setActionMessage] = useState<string | null>(null);
  const [viewingAsId, setViewingAsId] = useState<string | null>(null);

  const viewAs = async (memberId: string) => {
    setActionError(null);
    setActionMessage(null);
    setViewingAsId(memberId);
    try {
      await openViewAsSession(orgId, memberId);
      setActionMessage('Opened a view-as session in a new tab');
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to start view-as session');
    } finally {
      setViewingAsId(null);
    }
  };

  const roles = rolesQuery.data ?? [];
  useEffect(() => {
    if (!roleId && roles.length > 0) {
      const admin = roles.find((r) => r.id === 'built-in-admin' || r.name === 'Admin');
      setRoleId(admin?.id ?? roles[0].id);
    }
  }, [roles, roleId]);

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: membersKey });
  };

  const inviteMutation = useMutation({
    mutationFn: (body: InviteMemberBody) =>
      apiPost<{ message: string; member: OrgMemberRow }>(
        `/organizations/${orgId}/members`,
        body,
      ),
    onSuccess: (res) => {
      setActionError(null);
      setActionMessage(res.message);
      setInviteOpen(false);
      setFirstName('');
      setLastName('');
      setEmail('');
      setPhoneNumber('');
      setSkipInvitation(false);
      invalidate();
    },
    onError: (err: Error) => setActionError(err.message),
  });

  const patchMember = useMutation({
    mutationFn: ({
      memberId,
      body,
    }: {
      memberId: string;
      body: { roleId?: string };
    }) =>
      apiPatch<{ message: string; member: OrgMemberRow }>(
        `/organizations/${orgId}/members/${memberId}`,
        body,
      ),
    onSuccess: (res) => {
      setActionError(null);
      setActionMessage(res.message);
      invalidate();
    },
    onError: (err: Error) => setActionError(err.message),
  });

  const statusMutation = useMutation({
    mutationFn: ({
      memberId,
      body,
    }: {
      memberId: string;
      body: { status?: 'active' | 'inactive'; action?: 'resend_invitation' | 'cancel_invitation' };
    }) =>
      apiPatch<{ message: string; member: OrgMemberRow }>(
        `/organizations/${orgId}/members/${memberId}`,
        body,
      ),
    onSuccess: (res) => {
      setActionError(null);
      setActionMessage(res.message);
      invalidate();
    },
    onError: (err: Error) => setActionError(err.message),
  });

  const removeMutation = useMutation({
    mutationFn: (memberId: string) =>
      apiDelete<{ message: string }>(`/organizations/${orgId}/members/${memberId}`),
    onSuccess: (res) => {
      setActionError(null);
      setActionMessage(res.message);
      invalidate();
    },
    onError: (err: Error) => setActionError(err.message),
  });

  const members = membersQuery.data?.members ?? [];
  const roleName = (id: string) => roles.find((r) => r.id === id)?.name ?? id;
  const busy =
    inviteMutation.isPending ||
    patchMember.isPending ||
    statusMutation.isPending ||
    removeMutation.isPending;

  const submitInvite = () => {
    setActionError(null);
    if (!firstName.trim() || !lastName.trim() || !roleId) {
      setActionError('First name, last name, and role are required');
      return;
    }
    if (!skipInvitation && !email.trim() && !phoneNumber.trim()) {
      setActionError('Email or phone is required to send an invitation');
      return;
    }
    const body: InviteMemberBody = {
      firstName: firstName.trim(),
      lastName: lastName.trim(),
      roleId,
      skipInvitation: skipInvitation || undefined,
    };
    if (email.trim()) body.email = email.trim();
    if (phoneNumber.trim()) body.phoneNumber = phoneNumber.trim();
    inviteMutation.mutate(body);
  };

  return (
    <Section
      title="Team members"
      subtitle="Invite, reassign roles, and deactivate members for this company"
    >
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={() => {
            setInviteOpen((v) => !v);
            setActionError(null);
            setActionMessage(null);
          }}
          className="inline-flex items-center gap-1.5 rounded-md bg-forest px-3 py-1.5 text-sm font-medium text-white transition-colors hover:bg-forest/90"
        >
          <Plus className="h-4 w-4" weight="bold" />
          {inviteOpen ? 'Close' : 'Add member'}
        </button>
        {membersQuery.data && (
          <span className="text-xs text-stone-400">
            {membersQuery.data.count} members
            {membersQuery.data.totalByStatus
              ? ` · ${membersQuery.data.totalByStatus.active} active · ${membersQuery.data.totalByStatus.invited} invited`
              : ''}
          </span>
        )}
        {actionMessage && (
          <span className="text-sm font-medium text-emerald-700">{actionMessage}</span>
        )}
        {actionError && <span className="text-sm text-red-600">{actionError}</span>}
      </div>

      {inviteOpen && (
        <div className="mb-4 grid grid-cols-1 gap-3 rounded-lg border border-stone-200 bg-stone-50 p-3 sm:grid-cols-2">
          <Field label="First name">
            <input
              className={fieldClass}
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
            />
          </Field>
          <Field label="Last name">
            <input
              className={fieldClass}
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
            />
          </Field>
          <Field label="Email">
            <input
              type="email"
              className={fieldClass}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="optional if phone set"
            />
          </Field>
          <Field label="Phone">
            <input
              className={fieldClass}
              value={phoneNumber}
              onChange={(e) => setPhoneNumber(e.target.value)}
              placeholder="optional if email set"
            />
          </Field>
          <Field label="Role">
            <select
              className={fieldClass}
              value={roleId}
              onChange={(e) => setRoleId(e.target.value)}
            >
              {rolesQuery.isLoading && <option value="">Loading roles…</option>}
              {!rolesQuery.isLoading && roles.length === 0 && (
                <option value="">No roles available</option>
              )}
              {roles.map((r) => (
                <option key={r.id} value={r.id}>
                  {r.name}
                </option>
              ))}
            </select>
          </Field>
          <div className="flex items-end">
            <Toggle
              checked={skipInvitation}
              onChange={setSkipInvitation}
              label="Skip invitation"
              description="Add without sending email/SMS (member still needs to sign in)."
            />
          </div>
          <div className="sm:col-span-2">
            <button
              type="button"
              disabled={busy}
              onClick={submitInvite}
              className="inline-flex items-center gap-2 rounded-md bg-primary px-3 py-2 text-sm font-medium text-white transition-colors hover:bg-primary-2 disabled:opacity-50"
            >
              {inviteMutation.isPending && <CircleNotch className="h-4 w-4 animate-spin" />}
              {skipInvitation ? 'Add member' : 'Send invite'}
            </button>
          </div>
        </div>
      )}

      {membersQuery.isLoading && (
        <p className="py-4 text-center text-sm text-stone-400">Loading members…</p>
      )}
      {membersQuery.error && <ErrorState error={membersQuery.error} />}

      {!membersQuery.isLoading && !membersQuery.error && (
        <TableScroll>
          <table className="w-full min-w-[560px] whitespace-nowrap text-sm">
            <thead>
              <tr className="border-b border-stone-200 text-left text-[11px] uppercase tracking-wider text-stone-400">
                <th className="py-1.5 font-medium">Name</th>
                <th className="py-1.5 font-medium">Contact</th>
                <th className="py-1.5 font-medium">Role</th>
                <th className="py-1.5 font-medium">Status</th>
                <th className="py-1.5 text-right font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {members.length === 0 && (
                <tr>
                  <td colSpan={5} className="py-6 text-center text-stone-400">
                    No members yet
                  </td>
                </tr>
              )}
              {members.map((m) => (
                <tr key={m.id} className="border-b border-stone-100">
                  <td className="py-2 font-medium text-stone-800">{memberDisplayName(m)}</td>
                  <td className="py-2 text-stone-600">
                    <div className="flex flex-col gap-0.5">
                      {m.email && <span>{m.email}</span>}
                      {m.phoneNumber && <span className="text-xs text-stone-400">{m.phoneNumber}</span>}
                      {!m.email && !m.phoneNumber && <span className="text-stone-400">—</span>}
                    </div>
                  </td>
                  <td className="py-2">
                    <select
                      className={clsx(fieldClass, 'max-w-[10rem] py-1')}
                      value={m.roleId}
                      disabled={busy}
                      onChange={(e) => {
                        const next = e.target.value;
                        if (next === m.roleId) return;
                        patchMember.mutate({ memberId: m.id, body: { roleId: next } });
                      }}
                    >
                      {/* Ensure current role is selectable even if roles still loading */}
                      {!roles.some((r) => r.id === m.roleId) && (
                        <option value={m.roleId}>{roleName(m.roleId)}</option>
                      )}
                      {roles.map((r) => (
                        <option key={r.id} value={r.id}>
                          {r.name}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td className="py-2">
                    <StatusBadge
                      value={m.status}
                      positive={m.status === 'active'}
                    />
                  </td>
                  <td className="py-2 text-right">
                    <div className="inline-flex flex-wrap items-center justify-end gap-1">
                      {m.status === 'invited' && (
                        <>
                          <button
                            type="button"
                            disabled={busy}
                            onClick={() =>
                              statusMutation.mutate({
                                memberId: m.id,
                                body: { action: 'resend_invitation' },
                              })
                            }
                            className="rounded-md px-2 py-1 text-xs font-medium text-stone-600 hover:bg-stone-100 disabled:opacity-50"
                          >
                            Resend
                          </button>
                          <button
                            type="button"
                            disabled={busy}
                            onClick={() =>
                              statusMutation.mutate({
                                memberId: m.id,
                                body: { action: 'cancel_invitation' },
                              })
                            }
                            className="rounded-md px-2 py-1 text-xs font-medium text-stone-600 hover:bg-stone-100 disabled:opacity-50"
                          >
                            Cancel invite
                          </button>
                        </>
                      )}
                      {m.status === 'active' && (
                        <>
                          <button
                            type="button"
                            disabled={busy || viewingAsId !== null}
                            onClick={() => void viewAs(m.id)}
                            className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium text-forest hover:bg-forest/10 disabled:opacity-50"
                            title="Open the office app signed in as this user — anything you change there is real"
                          >
                            {viewingAsId === m.id && <CircleNotch className="h-3 w-3 animate-spin" />}
                            View as
                          </button>
                          <button
                            type="button"
                            disabled={busy}
                            onClick={() =>
                              statusMutation.mutate({
                                memberId: m.id,
                                body: { status: 'inactive' },
                              })
                            }
                            className="rounded-md px-2 py-1 text-xs font-medium text-stone-600 hover:bg-stone-100 disabled:opacity-50"
                          >
                            Deactivate
                          </button>
                        </>
                      )}
                      {m.status === 'inactive' && (
                        <button
                          type="button"
                          disabled={busy}
                          onClick={() =>
                            statusMutation.mutate({
                              memberId: m.id,
                              body: { status: 'active' },
                            })
                          }
                          className="rounded-md px-2 py-1 text-xs font-medium text-stone-600 hover:bg-stone-100 disabled:opacity-50"
                        >
                          Reactivate
                        </button>
                      )}
                      <button
                        type="button"
                        disabled={busy}
                        title="Remove member"
                        onClick={() => {
                          if (
                            !window.confirm(
                              `Remove ${memberDisplayName(m)} from this organization?`,
                            )
                          ) {
                            return;
                          }
                          removeMutation.mutate(m.id);
                        }}
                        className="inline-flex items-center rounded-md p-1.5 text-red-600 hover:bg-red-50 disabled:opacity-50"
                      >
                        <Trash className="h-3.5 w-3.5" weight="bold" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </TableScroll>
      )}
    </Section>
  );
}

// ---------------------------------------------------------------------------
// Usage section
// ---------------------------------------------------------------------------

function UsageSection({
  org,
  months,
  selectedMonth,
  windowMonths,
  onMonthsChange,
  onMonthChange,
  figures,
}: {
  org: OrgRow;
  months: string[];
  selectedMonth: string | null;
  windowMonths: number;
  onMonthsChange: (n: number) => void;
  onMonthChange: (m: string | null) => void;
  figures: {
    visitCount: number;
    revenue: number;
    paidRevenue: number;
    processingFees: number;
  };
}) {
  const sortedMonths = useMemo(() => [...months].sort((a, b) => b.localeCompare(a)), [months]);

  return (
    <Section
      title="Usage"
      subtitle={
        selectedMonth
          ? `Summary for ${formatMonth(selectedMonth)}`
          : `Totals over the last ${windowMonths} months`
      }
    >
      <div className="mb-4 flex flex-wrap items-end gap-4">
        <MonthsSelect
          value={windowMonths}
          onChange={onMonthsChange}
          variant="light"
          label="Trend window"
        />
        <label className="inline-flex items-center gap-2 text-xs text-stone-500">
          <span className="font-medium uppercase tracking-wider">Summary month</span>
          <select
            value={selectedMonth ?? ''}
            onChange={(e) => onMonthChange(e.target.value || null)}
            className="rounded-md border border-stone-200 bg-white px-2.5 py-1.5 text-sm text-stone-700 outline-none transition-colors hover:border-stone-300 focus:border-primary/40 focus:ring-2 focus:ring-primary/10"
          >
            <option value="">All months</option>
            {sortedMonths.map((m) => (
              <option key={m} value={m}>
                {formatMonth(m)}
              </option>
            ))}
          </select>
        </label>
      </div>

      <div className="mb-5 grid grid-cols-2 gap-3 lg:grid-cols-4">
        <StatCard label="Visits" value={formatCount(figures.visitCount)} />
        <StatCard label="Revenue" value={formatCurrency(figures.revenue)} />
        <StatCard label="Collected" value={formatCurrency(figures.paidRevenue)} />
        <StatCard label="Proc. fees" value={formatCurrency(figures.processingFees)} />
      </div>

      <TableScroll>
        <table className="w-full min-w-[440px] whitespace-nowrap text-sm">
          <thead>
            <tr className="border-b border-stone-200 text-left text-[11px] uppercase tracking-wider text-stone-400">
              <th className="py-1.5 font-medium">Month</th>
              <th className="py-1.5 text-right font-medium">Visits</th>
              <th className="py-1.5 text-right font-medium">Revenue</th>
              <th className="py-1.5 text-right font-medium">Collected</th>
              <th className="py-1.5 text-right font-medium">Proc. fees</th>
              <th className="py-1.5 text-right font-medium">MRR</th>
            </tr>
          </thead>
          <tbody>
            {org.byMonth.map((m) => (
              <tr
                key={m.month}
                className={clsx(
                  'border-b border-stone-100',
                  m.month === selectedMonth && 'bg-primary/10',
                )}
              >
                <td className="py-1.5 font-medium text-stone-700">{formatMonth(m.month)}</td>
                <td className="tabular py-1.5 text-right text-stone-700">
                  {formatCount(m.visitCount)}
                </td>
                <td className="tabular py-1.5 text-right text-stone-700">
                  {formatCurrency(m.revenue)}
                </td>
                <td className="tabular py-1.5 text-right text-stone-700">
                  {formatCurrency(m.paidRevenue)}
                </td>
                <td className="tabular py-1.5 text-right text-stone-700">
                  {formatCurrency(m.processingFees)}
                </td>
                <td className="tabular py-1.5 text-right text-stone-700">
                  {m.totalMRR === null ? '—' : formatCurrency(m.totalMRR)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </TableScroll>
    </Section>
  );
}

// ---------------------------------------------------------------------------
// Account page (RUT-4072)
// ---------------------------------------------------------------------------

type AccountTab = 'overview' | 'invoices' | 'usage' | 'team' | 'settings';

const TABS: Array<{ key: AccountTab; label: string }> = [
  { key: 'overview', label: 'Overview' },
  { key: 'invoices', label: 'Invoices' },
  { key: 'usage', label: 'Usage' },
  { key: 'team', label: 'Team' },
  { key: 'settings', label: 'Settings' },
];

/** The window status, health and money read — the same as the Organizations list. */
const ACCOUNT_WINDOW_MONTHS = 6;

/** How many invoices the overview shows before "All invoices". */
const OVERVIEW_INVOICES = 6;

function formatDay(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' });
}

function invoicePeriod(invoice: OrgInvoice): string {
  if (invoice.periodStart) return formatMonth(invoice.periodStart.slice(0, 7));
  return formatMonth(invoice.billedAt.slice(0, 7));
}

function InvoicesTable({ invoices }: { invoices: OrgInvoice[] }) {
  if (invoices.length === 0) {
    return <p className="py-6 text-center text-sm text-stone-400">We have not invoiced them yet.</p>;
  }
  return (
    <TableScroll>
      <table className="w-full min-w-[640px] text-sm">
        <thead>
          <tr className="border-b border-stone-200 text-left text-[11px] uppercase tracking-wider text-stone-400">
            <th className="py-2 pr-3 font-medium">Invoice</th>
            <th className="py-2 pr-3 font-medium">Period</th>
            <th className="py-2 pr-3 font-medium">What it covers</th>
            <th className="py-2 pr-3 text-right font-medium">Amount</th>
            <th className="py-2 pr-3 font-medium">Status</th>
            <th className="py-2 font-medium">Billed</th>
          </tr>
        </thead>
        <tbody>
          {invoices.map((invoice) => (
            <tr key={invoice.id} className="border-b border-stone-100 align-top">
              <td className="whitespace-nowrap py-2 pr-3 font-mono text-xs text-stone-700">{invoice.invoiceNumber}</td>
              <td className="whitespace-nowrap py-2 pr-3 text-stone-700">{invoicePeriod(invoice)}</td>
              <td className="py-2 pr-3 text-stone-700">
                {invoice.planName ?? (invoice.billingType === 'activation' ? 'Activation' : 'Plan')}
              </td>
              <td className="tabular whitespace-nowrap py-2 pr-3 text-right font-medium text-stone-900">
                {formatCurrency(invoice.total)}
              </td>
              <td className="py-2 pr-3">
                {invoice.status === 'paid' ? (
                  <Pill tone="ok">Paid</Pill>
                ) : (
                  <>
                    <Pill tone="risk">Open</Pill>
                    {invoice.openReason && <div className="mt-1 text-xs text-stone-500">{invoice.openReason}</div>}
                  </>
                )}
              </td>
              <td className="whitespace-nowrap py-2 text-stone-600">{formatDay(invoice.billedAt)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </TableScroll>
  );
}

const MISSING_FIELD_LABEL: Record<string, string> = {
  legalName: 'legal name',
  taxId: 'EIN',
  'address.line1': 'address',
  'address.city': 'city',
  'address.state': 'state',
  'address.postalCode': 'postal code',
  website: 'website',
  contactEmail: 'contact email',
  phoneNumber: 'phone number',
  paymentMethodId: 'card on file',
};

const RCS_STATUS_LABEL: Record<RcsStatus, string> = {
  off: 'off',
  pending: 'pending',
  live: 'live',
  carrier_launched: 'carrier launched',
};

/** Mirrors `normalizeRcsStatus` — admin does not depend on `@ruta/shared`. */
function displayRcsStatus(status: string | undefined): RcsStatus {
  if (status === 'business_verified' || status === 'carrier_launched') return 'carrier_launched';
  if (status === 'pending' || status === 'live') return status;
  return 'off';
}

function RcsUpgradePanel({ org }: { org: OrgRow }) {
  const queryClient = useQueryClient();
  const [result, setResult] = useState<RcsUpgradeActionResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [confirmKind, setConfirmKind] = useState<'upgrade' | 'carrier_launch' | null>(null);

  const preview = useMutation({
    mutationFn: (kind: 'upgrade' | 'carrier_launch') =>
      apiPost<RcsUpgradeActionResult>(`/organizations/${org.orgId}/rcs-upgrades`, { kind, confirm: false }),
    onSuccess: (data, kind) => {
      setError(null);
      setResult(data);
      const plan = data.plan;
      setConfirmKind(plan.missing.length === 0 && !plan.blockedReason ? kind : null);
    },
    onError: (err: Error) => {
      setError(err.message);
    },
  });

  const confirm = useMutation({
    mutationFn: (kind: 'upgrade' | 'carrier_launch') =>
      apiPost<RcsUpgradeActionResult>(`/organizations/${org.orgId}/rcs-upgrades`, { kind, confirm: true }),
    onSuccess: (data) => {
      setError(null);
      setResult(data);
      setConfirmKind(null);
      void queryClient.invalidateQueries({ queryKey: ['organizations'] });
    },
    onError: (err: Error) => {
      setError(err.message);
    },
  });

  const status = displayRcsStatus(org.rcsRegistrationStatus?.status);
  const campaignApproved = org.messagingRegistrationStatus?.status === 'approved';
  const busy = preview.isPending || confirm.isPending;
  const plan = result?.plan;
  const missing = plan?.missing ?? [];
  const canConfirm =
    result?.mode === 'preview' &&
    confirmKind &&
    missing.length === 0 &&
    !plan?.blockedReason &&
    !plan?.alreadyCharged;
  const feeLines = org.rcsFeeLines ?? [];
  const declineCopy = org.rcsDeclineCopy;

  return (
    <div className="mt-3 rounded-lg border border-stone-200 p-3" data-testid="rcs-upgrade">
      <div className="text-[11px] font-medium uppercase tracking-wider text-stone-400">RCS</div>
      <div className="mt-2 divide-y divide-stone-100">
        <KeyValueRow label="Status" value={RCS_STATUS_LABEL[status]} />
        {feeLines.map((line) => (
          <div key={line.kind} data-testid={`rcs-fee-${line.kind}`}>
            <KeyValueRow
              label={line.label}
              value={`${line.cadence} · ${line.detail}`}
            />
          </div>
        ))}
      </div>
      {declineCopy && (
        <p className="mt-2 text-xs text-red-700" data-testid="rcs-upgrade-decline">
          {declineCopy}
        </p>
      )}
      {org.rcsRegistrationStatus?.failureReason && !declineCopy && (
        <p className="mt-2 text-xs text-stone-600" data-testid="rcs-upgrade-flag">
          {org.rcsRegistrationStatus.failureReason}
        </p>
      )}
      {org.rcsRegistrationStatus?.refundFailed && (
        <p className="mt-2 text-xs text-red-700">Charge succeeded but the refund failed — office needs to act.</p>
      )}

      {!campaignApproved && (
        <p className="mt-2 text-xs text-stone-600" data-testid="rcs-upgrade-gated">
          This shop&apos;s own texting campaign is not approved yet. File and wait for approval first.
        </p>
      )}

      <div className="mt-3 flex flex-wrap items-center gap-2">
        <button
          type="button"
          disabled={busy || !campaignApproved}
          onClick={() => preview.mutate('upgrade')}
          data-testid="rcs-upgrade-start"
          className="inline-flex items-center gap-2 rounded-md bg-primary px-3 py-2 text-sm font-medium text-white transition-colors hover:bg-primary-2 disabled:opacity-50"
        >
          {preview.isPending && preview.variables === 'upgrade' && <CircleNotch className="h-4 w-4 animate-spin" />}
          Upgrade to RCS
        </button>
        <button
          type="button"
          disabled={busy || !campaignApproved || status === 'off'}
          onClick={() => preview.mutate('carrier_launch')}
          data-testid="rcs-upgrade-carrier-launch"
          className="inline-flex items-center gap-2 rounded-md border border-stone-200 bg-white px-3 py-2 text-sm font-medium text-stone-800 transition-colors hover:border-stone-300 disabled:opacity-50"
        >
          {preview.isPending && preview.variables === 'carrier_launch' && <CircleNotch className="h-4 w-4 animate-spin" />}
          Carrier launch
        </button>
        {canConfirm && confirmKind && (
          <button
            type="button"
            disabled={busy}
            onClick={() => confirm.mutate(confirmKind)}
            data-testid="rcs-upgrade-confirm"
            className="inline-flex items-center gap-2 rounded-md border border-stone-200 bg-white px-3 py-2 text-sm font-medium text-stone-800 transition-colors hover:border-stone-300 disabled:opacity-50"
          >
            {confirm.isPending && <CircleNotch className="h-4 w-4 animate-spin" />}
            Confirm {formatCurrency(plan?.amountUsd ?? 0)}
          </button>
        )}
      </div>

      {error && <p className="mt-2 text-xs text-red-700">{error}</p>}

      {result && (
        <div className="mt-3 text-sm text-stone-700" data-testid="rcs-upgrade-preview">
          {plan?.blockedReason ? (
            <p>{plan.blockedReason}</p>
          ) : missing.length > 0 ? (
            <div>
              <p className="font-medium text-stone-800">Missing — nothing charged</p>
              <ul className="mt-1 list-disc pl-5 text-xs text-stone-600">
                {missing.map((field) => (
                  <li key={field}>{MISSING_FIELD_LABEL[field] ?? field}</li>
                ))}
              </ul>
            </div>
          ) : result.mode === 'charged' ? (
            <p>
              {result.rcsRegistrationStatus?.refundFailed
                ? result.rcsRegistrationStatus.failureReason
                : `Charged ${formatCurrency(plan?.amountUsd ?? 0)}. Status ${RCS_STATUS_LABEL[result.rcsRegistrationStatus?.status ?? 'off']}.`}
            </p>
          ) : (
            <div data-testid="rcs-upgrade-confirm-copy">
              <p className="font-medium text-stone-800">
                Charge {formatCurrency(plan?.amountUsd ?? 0)} to the card on file
                {plan?.cardLast4 ? ` ····${plan.cardLast4}` : ''} — nothing charged yet
              </p>
              <p className="mt-1 text-xs text-stone-600">{plan?.costSource}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function FileTextingCampaignPanel({ org }: { org: OrgRow }) {
  const queryClient = useQueryClient();
  const [result, setResult] = useState<OwnBrandFilingActionResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const dryRun = useMutation({
    mutationFn: () => apiPost<OwnBrandFilingActionResult>(`/organizations/${org.orgId}/texting-campaigns`, { confirm: false }),
    onSuccess: (data) => {
      setError(null);
      setResult(data);
    },
    onError: (err: Error) => {
      setError(err.message);
    },
  });

  const confirm = useMutation({
    mutationFn: () => apiPost<OwnBrandFilingActionResult>(`/organizations/${org.orgId}/texting-campaigns`, { confirm: true }),
    onSuccess: (data) => {
      setError(null);
      setResult(data);
      void queryClient.invalidateQueries({ queryKey: ['organizations'] });
    },
    onError: (err: Error) => {
      setError(err.message);
    },
  });

  const reg = org.messagingRegistrationStatus;
  const plan = result?.plan;
  const missing = plan?.missing ?? [];
  const canConfirm = result?.mode === 'preview' && missing.length === 0 && !plan?.lookupError;
  const busy = dryRun.isPending || confirm.isPending;

  return (
    <div className="mt-3 rounded-lg border border-stone-200 p-3" data-testid="file-texting-campaign">
      <div className="text-[11px] font-medium uppercase tracking-wider text-stone-400">File texting campaign</div>
      <div className="mt-2 divide-y divide-stone-100">
        <KeyValueRow label="Status" value={reg?.status ?? 'unset'} />
        <KeyValueRow label="Brand" value={reg?.brandId ?? 'none'} />
        <KeyValueRow label="Campaign" value={reg?.campaignId ?? 'none'} />
      </div>
      {reg?.failureReason && <p className="mt-2 text-xs text-stone-600">{reg.failureReason}</p>}

      <div className="mt-3 flex flex-wrap items-center gap-2">
        <button
          type="button"
          disabled={busy}
          onClick={() => dryRun.mutate()}
          className="inline-flex items-center gap-2 rounded-md bg-primary px-3 py-2 text-sm font-medium text-white transition-colors hover:bg-primary-2 disabled:opacity-50"
        >
          {dryRun.isPending && <CircleNotch className="h-4 w-4 animate-spin" />}
          File texting campaign
        </button>
        {canConfirm && (
          <button
            type="button"
            disabled={busy}
            onClick={() => confirm.mutate()}
            data-testid="file-texting-confirm"
            className="inline-flex items-center gap-2 rounded-md border border-stone-200 bg-white px-3 py-2 text-sm font-medium text-stone-800 transition-colors hover:border-stone-300 disabled:opacity-50"
          >
            {confirm.isPending && <CircleNotch className="h-4 w-4 animate-spin" />}
            Confirm
          </button>
        )}
      </div>

      {error && <p className="mt-2 text-xs text-red-700">{error}</p>}

      {result && (
        <div className="mt-3 text-sm text-stone-700" data-testid="file-texting-dry-run">
          {missing.length > 0 ? (
            <div data-testid="file-texting-missing">
              <p className="font-medium text-stone-800">Missing fields — nothing filed</p>
              <ul className="mt-1 list-disc pl-5 text-xs text-stone-600">
                {missing.map((field) => (
                  <li key={field}>{MISSING_FIELD_LABEL[field] ?? field}</li>
                ))}
              </ul>
            </div>
          ) : result.mode === 'filed' ? (
            <p>
              Filed. Brand {result.messagingRegistrationStatus?.brandId ?? '—'}, campaign{' '}
              {result.campaignId ?? result.messagingRegistrationStatus?.campaignId ?? '—'}. Status{' '}
              {result.messagingRegistrationStatus?.status ?? '—'}.
            </p>
          ) : (
            <div>
              <p className="font-medium text-stone-800">Dry run — nothing filed</p>
              <ul className="mt-1 list-disc pl-5 text-xs text-stone-600">
                <li>
                  {plan?.reuseBrandId
                    ? `Reuse brand ${plan.reuseBrandId}`
                    : 'File a new brand'}
                </li>
                <li>
                  {plan?.reuseCampaignId
                    ? `Reuse MIXED campaign ${plan.reuseCampaignId}`
                    : 'File one MIXED campaign'}
                </li>
              </ul>
              {plan?.lookupError && <p className="mt-1 text-xs text-stone-600">{plan.lookupError}</p>}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function VendorBlock({
  title,
  status,
  now,
  footnote,
}: {
  title: string;
  status: OrgRow['status']['justifi'] | OrgRow['status']['telnyx'];
  now: Date;
  footnote?: string;
}) {
  const sub = statusSubline(status, now);
  const held = durationLabel(status.since, now);
  return (
    <div className="rounded-lg border border-stone-200 p-3">
      <div className="text-[11px] font-medium uppercase tracking-wider text-stone-400">{title}</div>
      <div className="mt-1.5">
        <Pill tone={vendorTone(status.bucket, status.waitingOn)}>{status.label}</Pill>
      </div>
      <div className="mt-1.5 text-xs text-stone-500">
        {sub ?? (held ? `For ${held.toLowerCase()}` : 'Nobody is waiting on anyone')}
      </div>
      {status.detail && <div className="mt-1 text-xs text-stone-600">{status.detail}</div>}
      {footnote && <div className="mt-2 text-[11px] leading-relaxed text-stone-400">{footnote}</div>}
    </div>
  );
}

function AccountOverview({
  org,
  month,
  months,
  overview,
  moneyLoading,
  onAllInvoices,
}: {
  org: OrgRow;
  month: string;
  months: string[];
  overview: OverviewResponse | undefined;
  moneyLoading: boolean;
  onAllInvoices: () => void;
}) {
  const now = new Date();
  // Only a month Home counts carries money, the same rule as the Organizations row.
  const found = moneyFor(overview, org.orgId, month);
  const money = found && found.counted ? found : null;
  const monthLabel = formatMonth(month);
  const pending = moneyLoading ? '…' : '—';
  const pct = money ? marginPct(money.revenueToRuta, money.subscriptionMargin) : null;
  const billed = org.billedToDate;
  const change = org.status.visitChange;

  return (
    <>
      <div className="mb-6 grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <StatCard
          label={`Revenue to Ruta, ${monthLabel}`}
          value={money ? formatCurrency(money.revenueToRuta) : pending}
          hint={
            money
              ? `Invoiced ${formatCurrency(money.invoiced)} + Ruta Pay spread ${formatCurrency(money.rutaPaySpread)}`
              : undefined
          }
        />
        <StatCard
          label={`Direct cost, ${monthLabel}`}
          value={money ? formatCurrency(money.directCost.total) : pending}
          hint={
            money
              ? `Texts ${formatCurrency(money.directCost.texts)} · visits ${formatCurrency(money.directCost.visits)} · AI ${formatCurrency(money.directCost.ai)}`
              : undefined
          }
        />
        <StatCard
          label="Subscription margin"
          value={money ? formatCurrency(money.subscriptionMargin) : pending}
          hint={money ? `${formatPercent(pct, 0)} · shared costs not included` : undefined}
        />
        <StatCard
          label="Billed to date"
          value={formatCurrency(billed.total)}
          hint={
            billed.count === 0
              ? 'No invoices yet'
              : billed.openCount === 0
                ? `${formatCount(billed.count)} invoices, all paid`
                : `${formatCount(billed.count)} invoices, ${formatCount(billed.openCount)} open (${formatCurrency(billed.open)})`
          }
        />
      </div>

      <Section title="Signals" subtitle="Where they stand with our vendors, what is stuck, and how their use is moving">
        <div className="mb-4 flex flex-wrap items-center gap-2 text-sm text-stone-700">
          <span className="text-[11px] font-medium uppercase tracking-wider text-stone-400">Health</span>
          <HealthPill level={org.status.health.level} />
          {org.status.health.reason && <span>{org.status.health.reason}</span>}
        </div>
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          <VendorBlock title="JustiFi — Ruta Pay" status={org.status.justifi} now={now} />
          <VendorBlock
            title="Telnyx — own texting number"
            status={org.status.telnyx}
            now={now}
            footnote="Their texts still go out on Ruta's shared number, whatever this says."
          />
        </div>
        <FileTextingCampaignPanel org={org} />
        <RcsUpgradePanel org={org} />

        <div className="mt-4">
          <div className="mb-1.5 text-[11px] font-medium uppercase tracking-wider text-stone-400">What is stuck</div>
          {org.status.attention.length === 0 ? (
            <p className="text-sm text-stone-500">Nothing is stuck.</p>
          ) : (
            <ul className="divide-y divide-stone-100">
              {org.status.attention.map((item, index) => {
                const days = durationLabel(item.since, now);
                return (
                  <li key={`${item.kind}-${index}`} className="flex flex-wrap items-baseline justify-between gap-2 py-2">
                    <div className="min-w-0">
                      <div className="text-sm font-medium text-stone-800">{item.title}</div>
                      {item.detail && <div className="text-xs text-stone-500">{item.detail}</div>}
                    </div>
                    <div className="text-xs text-stone-500">
                      {[days, WAITING_ON_SHORT[item.waitingOn]].filter(Boolean).join(' · ')}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        <div className="mt-4">
          <div className="mb-1.5 flex flex-wrap items-baseline justify-between gap-2">
            <span className="text-[11px] font-medium uppercase tracking-wider text-stone-400">By month</span>
            {change !== null && (
              <span className={clsx('text-xs', change <= -0.5 ? 'font-medium text-red-700' : 'text-stone-500')}>
                Visits last month {change >= 0 ? '+' : ''}
                {formatPercent(change, 0)} against the months before
              </span>
            )}
          </div>
          <TableScroll>
            <table className="w-full min-w-[560px] text-sm">
              <thead>
                <tr className="border-b border-stone-200 text-left text-[11px] uppercase tracking-wider text-stone-400">
                  <th className="py-1.5 pr-3 font-medium">Month</th>
                  <th className="py-1.5 pr-3 text-right font-medium">Visits</th>
                  <th className="py-1.5 pr-3 text-right font-medium">Card revenue</th>
                  <th className="py-1.5 pr-3 text-right font-medium">Fee</th>
                  <th className="py-1.5 pr-3 text-right font-medium">To Ruta</th>
                  <th className="py-1.5 text-right font-medium">Margin</th>
                </tr>
              </thead>
              <tbody>
                {[...months].reverse().map((m) => {
                  const usage = org.byMonth.find((row) => row.month === m);
                  const foundMoney = moneyFor(overview, org.orgId, m);
                  const rowMoney = foundMoney && foundMoney.counted ? foundMoney : null;
                  return (
                    <tr key={m} className={clsx('border-b border-stone-100', m === month && 'bg-primary/5')}>
                      <td className="py-1.5 pr-3 font-medium text-stone-700">{formatMonth(m)}</td>
                      <td className="tabular py-1.5 pr-3 text-right text-stone-700">
                        {formatCount(usage?.visitCount ?? 0)}
                      </td>
                      <td className="tabular py-1.5 pr-3 text-right text-stone-700">
                        {formatCurrency(usage?.onlineRevenue ?? 0)}
                      </td>
                      <td className="tabular py-1.5 pr-3 text-right text-stone-700">
                        {usage?.platformFee == null ? '—' : formatCurrency(usage.platformFee)}
                      </td>
                      <td className="tabular py-1.5 pr-3 text-right text-stone-700">
                        {rowMoney ? formatCurrency(rowMoney.revenueToRuta) : pending}
                      </td>
                      <td className="tabular py-1.5 text-right text-stone-700">
                        {rowMoney
                          ? formatPercent(marginPct(rowMoney.revenueToRuta, rowMoney.subscriptionMargin), 0)
                          : pending}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </TableScroll>
        </div>
      </Section>

      <Section title="Invoices" subtitle="Invoices we issued them, not estimates — newest first">
        <InvoicesTable invoices={org.invoices.slice(0, OVERVIEW_INVOICES)} />
        {org.invoices.length > OVERVIEW_INVOICES && (
          <button
            type="button"
            onClick={onAllInvoices}
            className="mt-3 text-xs font-medium text-primary hover:underline"
          >
            All {formatCount(org.invoices.length)} invoices
          </button>
        )}
      </Section>
    </>
  );
}

function AccountHeaderActions({ org }: { org: OrgRow }) {
  const actions = useCustomerActions(org);
  const [message, setMessage] = useState<string | null>(null);
  const viewAs = actions.find((a) => a.key === 'view-as')!;
  const run = async () => {
    try {
      const result = await viewAs.run();
      setMessage(typeof result === 'string' ? result : null);
    } catch (err) {
      setMessage(err instanceof Error ? err.message : 'Could not open the office app');
    }
  };
  return (
    <div className="flex flex-wrap items-center gap-2">
      {message && <span className="text-xs text-white/70">{message}</span>}
      <button
        type="button"
        onClick={() => void run()}
        title={viewAs.hint}
        className="rounded-md border border-white/15 bg-white/10 px-3 py-1.5 text-sm font-medium text-white transition-colors hover:bg-white/15"
      >
        View as office
      </button>
      <span className="rounded-md bg-white">
        <CustomerActionsMenu org={org} />
      </span>
    </div>
  );
}

/**
 * One customer's account (RUT-4072). Opens on what they bring in, what they
 * cost, their signals and their invoices; usage, team and the settings that
 * used to fill the Organizations page each sit on their own tab.
 */
export function OrganizationAccountPage() {
  const { orgId = '' } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const [months, setMonths] = useState(6);
  const [selectedMonth, setSelectedMonth] = useState<string | null>(null);
  const [usageMonth, setUsageMonth] = useState<string | null>(null);
  const tab = TABS.find((t) => t.key === searchParams.get('tab'))?.key ?? 'overview';

  // Money reads the list's six months and Ruta Pay history reads since signup,
  // so this page says what the Organizations row says; only the Usage tab
  // follows its own window.
  const { data, isLoading, error } = useInsightsQuery<OrganizationsResponse>(
    ['organizations', ACCOUNT_WINDOW_MONTHS],
    `/organizations?months=${ACCOUNT_WINDOW_MONTHS}`,
  );
  const usageQuery = useInsightsQuery<OrganizationsResponse>(
    ['organizations', months],
    `/organizations?months=${months}`,
  );
  const moneyQuery = useInsightsQuery<OverviewResponse>(
    ['overview', ACCOUNT_WINDOW_MONTHS],
    `/costs/platform?months=${ACCOUNT_WINDOW_MONTHS}`,
  );

  if (isLoading) return <LoadingState />;
  if (error || !data) return <ErrorState error={error} />;

  const org = data.organizations.find((o) => o.orgId === orgId);
  if (!org) {
    return (
      <>
        <Link to="/organizations" className="text-sm text-primary hover:underline">
          Organizations
        </Link>
        <div className="mt-4 rounded-xl border border-dashed border-stone-200 bg-white p-10 text-center text-sm text-stone-400">
          No company with ID {orgId}
        </div>
      </>
    );
  }

  const month =
    selectedMonth && data.months.includes(selectedMonth)
      ? selectedMonth
      : lastCompleteMonth(data.months, data.partialMonth);
  const setTab = (next: AccountTab) =>
    setSearchParams(
      (prev) => {
        const params = new URLSearchParams(prev);
        if (next === 'overview') params.delete('tab');
        else params.set('tab', next);
        return params;
      },
      { replace: true },
    );

  const currentFee = [...org.byMonth].reverse().find((m) => m.platformFee !== null);
  const headline = [
    org.status.justifi.bucket === 'live'
      ? 'Ruta Pay'
      : org.status.justifi.bucket === 'approved'
        ? 'Ruta Pay approved, unused'
        : 'No Ruta Pay',
    currentFee?.platformFee != null ? `${formatCurrency(currentFee.platformFee)} a month` : null,
    `on Ruta since ${formatDay(org.createdAt)}`,
    org.contactEmail ? `owner ${org.contactEmail}` : null,
  ]
    .filter(Boolean)
    .join(' · ');

  const usageData = usageQuery.data;
  const usageOrg = usageData?.organizations.find((o) => o.orgId === orgId);
  const usageActiveMonth = usageMonth && usageData?.months.includes(usageMonth) ? usageMonth : null;
  const usageMonthRow = usageActiveMonth ? usageOrg?.byMonth.find((x) => x.month === usageActiveMonth) : undefined;
  const usageFigures = usageActiveMonth
    ? {
        visitCount: usageMonthRow?.visitCount ?? 0,
        revenue: usageMonthRow?.revenue ?? 0,
        paidRevenue: usageMonthRow?.paidRevenue ?? 0,
        processingFees: usageMonthRow?.processingFees ?? 0,
      }
    : (usageOrg?.totals ?? org.totals);

  const billingDefaults: PlatformBillingPlan = data.defaults?.billingPlan ?? {
    basePrice: 150,
    priceWithoutPayments: 375,
    includedTexts: 0,
    starterTexts: 200,
    includedAiCredits: 0,
    topUpTextsPrice: 0.05,
    topUpTexts: 1,
    topUpAiCreditsPrice: 0,
    topUpAiCredits: 0,
    cardPercent: 3.99,
    cardFixed: 0.3,
    achPercent: 1,
    achCap: 25,
  };

  return (
    <>
      <nav className="mb-2 text-sm" aria-label="Breadcrumb">
        <Link to="/organizations" className="text-stone-500 hover:text-primary hover:underline">
          Organizations
        </Link>
        <span className="text-stone-300"> / </span>
        <span className="text-stone-700">{org.name}</span>
      </nav>
      <PageHeader
        title={org.name}
        subtitle={headline}
        actions={
          <>
            <HealthPill level={org.status.health.level} />
            <AccountHeaderActions org={org} />
          </>
        }
      />

      <div className="mb-5 flex flex-wrap items-end justify-between gap-3 border-b border-stone-200">
        <div className="-mb-px flex gap-1 overflow-x-auto" role="tablist" aria-label="Account">
          {TABS.map((t) => (
            <button
              key={t.key}
              type="button"
              role="tab"
              aria-selected={tab === t.key}
              onClick={() => setTab(t.key)}
              className={clsx(
                'whitespace-nowrap border-b-2 px-3 py-2 text-sm font-medium transition-colors',
                tab === t.key ? 'border-primary text-forest' : 'border-transparent text-stone-500 hover:text-stone-800',
              )}
            >
              {t.label}
            </button>
          ))}
        </div>
        {tab === 'overview' && (
          <label className="mb-2 inline-flex items-center gap-2 text-xs text-stone-500">
            <span className="font-medium uppercase tracking-wider">Month</span>
            <select
              value={month}
              onChange={(e) => setSelectedMonth(e.target.value)}
              className="rounded-md border border-stone-200 bg-white px-2.5 py-1.5 text-sm text-stone-700 outline-none transition-colors hover:border-stone-300 focus:border-primary/40 focus:ring-2 focus:ring-primary/10"
            >
              {[...data.months].reverse().map((m) => (
                <option key={m} value={m}>
                  {formatMonth(m)}
                  {m === data.partialMonth ? ' (so far)' : ''}
                </option>
              ))}
            </select>
          </label>
        )}
      </div>

      {tab === 'overview' && (
        <AccountOverview
          org={org}
          month={month}
          months={data.months}
          overview={moneyQuery.data}
          moneyLoading={moneyQuery.isLoading}
          onAllInvoices={() => setTab('invoices')}
        />
      )}
      {tab === 'invoices' && (
        <Section
          title="Invoices"
          subtitle={`${formatCount(org.billedToDate.count)} invoices · ${formatCurrency(org.billedToDate.total)} billed · ${formatCurrency(org.billedToDate.paid)} paid`}
        >
          <InvoicesTable invoices={org.invoices} />
        </Section>
      )}
      {tab === 'usage' && usageQuery.error && <ErrorState error={usageQuery.error} />}
      {tab === 'usage' && !usageQuery.error && !usageOrg && <LoadingState />}
      {tab === 'usage' && usageOrg && usageData && (
        <UsageSection
          org={usageOrg}
          months={usageData.months}
          selectedMonth={usageActiveMonth}
          windowMonths={months}
          onMonthsChange={setMonths}
          onMonthChange={setUsageMonth}
          figures={usageFigures}
        />
      )}
      {tab === 'team' && <MembersSection orgId={org.orgId} />}
      {tab === 'settings' && <OrgSettingsForm org={org} defaults={billingDefaults} />}
    </>
  );
}
